import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { I18nProvider } from "@/contexts/I18nContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppShell from "@/components/layout/AppShell";
import MemberShell from "@/components/layout/MemberShell";
import LoginPage from "@/pages/LoginPage";
import HomePage from "@/pages/HomePage";
import SearchResultsPage from "@/pages/SearchResultsPage";
import DealDetailPage from "@/pages/DealDetailPage";
import WhatsAppButton from "@/components/WhatsAppButton";
import { homeFor, ROLES } from "@/lib/roles";

import {
    AdminWallets, AdminCommissions, AdminAudit, AdminDisputes,
} from "@/pages/Placeholders";
import KioskRegisterPage from "@/pages/kiosk/KioskRegisterPage";
import KioskMembersPage from "@/pages/kiosk/KioskMembersPage";
import KioskProvidersPage from "@/pages/kiosk/KioskProvidersPage";
import KioskSalesPage from "@/pages/kiosk/KioskSalesPage";
import KioskWalletPage from "@/pages/kiosk/KioskWalletPage";
import ProviderDashboardPage from "@/pages/provider/ProviderDashboardPage";
import VerifyPage from "@/pages/provider/VerifyPage";
import VisitsPage from "@/pages/provider/VisitsPage";
import MemberVisitsPage from "@/pages/member/MemberVisitsPage";
import KycQueuePage from "@/pages/admin/KycQueuePage";
import ProviderKycPage from "@/pages/provider/KycPage";
import CategoriesPage from "@/pages/admin/CategoriesPage";
import CategoriesPreviewPage from "@/pages/admin/CategoriesPreviewPage";
import MembersPage from "@/pages/admin/MembersPage";
import ProvidersPage from "@/pages/admin/ProvidersPage";
import OffersPage from "@/pages/provider/OffersPage";
import AllOffersPage from "@/pages/admin/AllOffersPage";
import UsersPage from "@/pages/admin/UsersPage";
import ProviderTypesPage from "@/pages/admin/ProviderTypesPage";
import MemberHomePage from "@/pages/member/MemberHomePage";
import CategoryViewPage from "@/pages/member/CategoryViewPage";
import ProviderListingPage from "@/pages/member/ProviderListingPage";
import ProviderDetailPage from "@/pages/member/ProviderDetailPage";
import MemberCardPage from "@/pages/member/MemberCardPage";
import MemberDashboardPage from "@/pages/member/MemberDashboardPage";
import MyClaimsPage from "@/pages/member/MyClaimsPage";
import PendingActivationPage from "@/pages/member/PendingActivationPage";
import ProfilePage from "@/pages/provider/ProfilePage";
import LocationsPage from "@/pages/provider/LocationsPage";
import StaffPage from "@/pages/provider/StaffPage";
import SignupPage from "@/pages/SignupPage";
import StaffLoginPage from "@/pages/StaffLoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AboutUsPage from "@/pages/AboutUsPage";
import MedicalTourismPage from "@/pages/MedicalTourismPage";
import ContactUsPage from "@/pages/ContactUsPage";
import ForProvidersPage from "@/pages/ForProvidersPage";
import FAQPage from "@/pages/FAQPage";
import PublicProviderPage from "@/pages/PublicProviderPage";
import ProviderSignupPage from "@/pages/ProviderSignupPage";
import DashboardPage from "@/pages/admin/DashboardPage";

function RootRedirect() {
    const { user } = useAuth();
    if (user === null) {
        // Still loading auth state — show homepage
        return <HomePage />;
    }
    if (!user) return <HomePage />;
    // User is authenticated — redirect to their dashboard
    return <Navigate to={homeFor(user.role)} replace />;
}

const ALL_ADMIN = [ROLES.SUPER_ADMIN, ROLES.OPS_ADMIN, ROLES.FINANCE_ADMIN, ROLES.SALES_AGENT];
const FINANCE   = [ROLES.SUPER_ADMIN, ROLES.FINANCE_ADMIN];
const OPS       = [ROLES.SUPER_ADMIN, ROLES.OPS_ADMIN];
const PROVIDERS = [ROLES.SUPER_ADMIN, ROLES.PROVIDER_ADMIN];
const PROV_ANY  = [ROLES.SUPER_ADMIN, ROLES.PROVIDER_ADMIN, ROLES.PROVIDER_STAFF];
const SALES     = [ROLES.SUPER_ADMIN, ROLES.SALES_AGENT];

function App() {
    return (
        <I18nProvider>
            <AuthProvider>
                <BrowserRouter>
                    <Toaster richColors position="top-right" />
                    <WhatsAppButton />
                    <Routes>
                        <Route path="/" element={<RootRedirect />} />
                        <Route path="/home" element={<HomePage />} />
                        <Route path="/search" element={<SearchResultsPage />} />
                        <Route path="/deal/:id" element={<DealDetailPage />} />
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/staff/login" element={<StaffLoginPage />} />
                        <Route path="/signup" element={<SignupPage />} />
                        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                        <Route path="/reset-password" element={<ResetPasswordPage />} />
                        <Route path="/about" element={<AboutUsPage />} />
                        <Route path="/medical-tourism" element={<MedicalTourismPage />} />
                        <Route path="/contact" element={<ContactUsPage />} />
                        <Route path="/for-providers" element={<ForProvidersPage />} />
                        <Route path="/faq" element={<FAQPage />} />
                        <Route path="/provider-signup" element={<ProviderSignupPage />} />
                        <Route path="/providers/:id" element={<PublicProviderPage />} />

                        {/* Admin shell */}
                        <Route element={
                            <ProtectedRoute allowedRoles={ALL_ADMIN}>
                                <AppShell />
                            </ProtectedRoute>
                        }>
                            <Route path="/admin/dashboard"   element={<DashboardPage />} />
                            <Route path="/admin/kyc"         element={<ProtectedRoute allowedRoles={OPS}><KycQueuePage /></ProtectedRoute>} />
                            <Route path="/admin/categories"  element={<ProtectedRoute allowedRoles={[ROLES.SUPER_ADMIN]}><CategoriesPage /></ProtectedRoute>} />
                            <Route path="/admin/categories/preview"  element={<ProtectedRoute allowedRoles={[ROLES.SUPER_ADMIN]}><CategoriesPreviewPage /></ProtectedRoute>} />
                            <Route path="/admin/members"     element={<ProtectedRoute allowedRoles={OPS}><MembersPage /></ProtectedRoute>} />
                            <Route path="/admin/providers"   element={<ProtectedRoute allowedRoles={OPS}><ProvidersPage /></ProtectedRoute>} />
                            <Route path="/admin/offers"          element={<ProtectedRoute allowedRoles={OPS}><AllOffersPage /></ProtectedRoute>} />
                            <Route path="/admin/offers/pending"  element={<ProtectedRoute allowedRoles={OPS}><AllOffersPage defaultStatus="PENDING" /></ProtectedRoute>} />
                            <Route path="/admin/users"           element={<ProtectedRoute allowedRoles={[ROLES.SUPER_ADMIN]}><UsersPage /></ProtectedRoute>} />
                            <Route path="/admin/provider-types"  element={<ProtectedRoute allowedRoles={OPS}><ProviderTypesPage /></ProtectedRoute>} />
                            <Route path="/admin/wallets"     element={<ProtectedRoute allowedRoles={FINANCE}><AdminWallets /></ProtectedRoute>} />
                            <Route path="/admin/commissions" element={<ProtectedRoute allowedRoles={FINANCE}><AdminCommissions /></ProtectedRoute>} />
                            <Route path="/admin/audit"       element={<ProtectedRoute allowedRoles={[ROLES.SUPER_ADMIN]}><AdminAudit /></ProtectedRoute>} />
                            <Route path="/admin/disputes"    element={<ProtectedRoute allowedRoles={OPS}><AdminDisputes /></ProtectedRoute>} />
                        </Route>

                        {/* Provider shell */}
                        <Route element={
                            <ProtectedRoute allowedRoles={PROV_ANY}>
                                <AppShell />
                            </ProtectedRoute>
                        }>
                            <Route path="/provider/dashboard" element={<ProtectedRoute allowedRoles={PROVIDERS}><ProviderDashboardPage /></ProtectedRoute>} />
                            <Route path="/provider/profile"   element={<ProtectedRoute allowedRoles={PROVIDERS}><ProfilePage /></ProtectedRoute>} />
                            <Route path="/provider/locations" element={<ProtectedRoute allowedRoles={PROVIDERS}><LocationsPage /></ProtectedRoute>} />
                            <Route path="/provider/staff"     element={<ProtectedRoute allowedRoles={PROVIDERS}><StaffPage /></ProtectedRoute>} />
                            <Route path="/provider/offers"    element={<ProtectedRoute allowedRoles={PROVIDERS}><OffersPage /></ProtectedRoute>} />
                            <Route path="/provider/kyc"       element={<ProtectedRoute allowedRoles={PROVIDERS}><ProviderKycPage /></ProtectedRoute>} />
                            <Route path="/provider/verify"    element={<VerifyPage />} />
                            <Route path="/provider/visits"    element={<VisitsPage />} />
                        </Route>

                        {/* Kiosk / Sales shell */}
                        <Route element={
                            <ProtectedRoute allowedRoles={SALES}>
                                <AppShell />
                            </ProtectedRoute>
                        }>
                            <Route path="/kiosk/sales"     element={<KioskSalesPage />} />
                            <Route path="/kiosk/register"  element={<KioskRegisterPage />} />
                            <Route path="/kiosk/members"   element={<KioskMembersPage />} />
                            <Route path="/kiosk/providers" element={<KioskProvidersPage />} />
                            <Route path="/kiosk/wallet"    element={<KioskWalletPage />} />
                        </Route>

                        {/* Member shell — public site layout with sidebar */}
                        <Route element={
                            <ProtectedRoute allowedRoles={[ROLES.MEMBER]}>
                                <MemberShell />
                            </ProtectedRoute>
                        }>
                            <Route path="/member/dashboard"       element={<MemberDashboardPage />} />
                            <Route path="/member/home"            element={<MemberHomePage />} />
                            <Route path="/member/category/:id"    element={<CategoryViewPage />} />
                            <Route path="/member/providers/:l2Id" element={<ProviderListingPage />} />
                            <Route path="/member/provider/:id"    element={<ProviderDetailPage />} />
                            <Route path="/member/card"            element={<MemberCardPage />} />
                            <Route path="/member/claims"          element={<MyClaimsPage />} />
                            <Route path="/member/visits"          element={<MemberVisitsPage />} />
                            <Route path="/member/pending"         element={<PendingActivationPage />} />
                        </Route>

                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </BrowserRouter>
            </AuthProvider>
        </I18nProvider>
    );
}

export default App;
